import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from 'recharts';
import {
  Search,
  Activity, FileText, Share2, LogIn, MessageSquare,
  Star, Trash2, RefreshCw, Zap, Hash, AlertCircle,
} from 'lucide-react';
import { APP_CONFIG } from '../config/appConfig';
import { authFetch } from '../config/authConfig';
import { useAuth } from '../contexts/AuthContext';

const API = APP_CONFIG.BACKEND_URL;

const ACTIVITY_ICONS: Record<string, React.ElementType> = {
  query: Search,
  share: Share2,
  export: FileText,
  download: FileText,
  login: LogIn,
  logout: LogIn,
  favorite: Star,
  new_conversation: MessageSquare,
  delete_conversation: Trash2,
  rename_conversation: Activity,
};

interface Summary {
  total_sessions: number;
  total_queries: number;
}

interface UsageData {
  usado: number;
  limite: number;
  disponible: number;
}

interface Usage {
  sesiones: UsageData;
  consultas: UsageData;
  tokens: UsageData;
}

interface DailyData { date: string; consultas: number; }
interface SessionDailyData { date: string; sesiones: number; }
interface HourlyData { hour: string; consultas: number; }
interface SessionQueryData { session: string; consultas: number; fecha: string; }
interface ActivityItem { id: string; user: string; type: string; description: string; timestamp: string; }

function formatTime(ts: string) {
  if (!ts) return '';
  const d = new Date(ts);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Ahora';
  if (mins < 60) return `Hace ${mins} min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `Hace ${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'Ayer';
  return `Hace ${days}d`;
}

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function ProgressBar({ label, icon: Icon, used, limit, color, suffix = '' }: {
  label: string;
  icon: React.ElementType;
  used: number;
  limit: number;
  color: string;
  suffix?: string;
}) {
  const pct = limit > 0 ? Math.min((used / limit) * 100, 100) : 0;
  const isNearLimit = pct >= 80;
  const isAtLimit = pct >= 100;

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
            <Icon className="w-4 h-4" style={{ color }} />
          </div>
          <span className="text-sm font-medium text-gray-700">{label}</span>
        </div>
        <div className="text-right">
          <span className={`text-sm font-bold ${isAtLimit ? 'text-red-600' : isNearLimit ? 'text-amber-600' : 'text-gray-900'}`}>
            {formatNumber(used)}
          </span>
          <span className="text-xs text-gray-400"> / {formatNumber(limit)} {suffix}</span>
        </div>
      </div>
      <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className="h-full rounded-full"
          style={{ backgroundColor: isAtLimit ? '#EF4444' : isNearLimit ? '#F59E0B' : color }}
        />
      </div>
      <div className="flex items-center justify-between mt-1.5">
        <span className="text-[10px] text-gray-400">{Math.round(pct)}% utilizado</span>
        {isAtLimit ? (
          <span className="flex items-center gap-1 text-[10px] text-red-500 font-medium">
            <AlertCircle className="w-3 h-3" /> Límite alcanzado
          </span>
        ) : isNearLimit ? (
          <span className="text-[10px] text-amber-500 font-medium">Cerca del límite</span>
        ) : (
          <span className="text-[10px] text-gray-400">{formatNumber(limit - used)} disponibles</span>
        )}
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const userId = user?.id || '';

  const [period, setPeriod] = useState<'7d' | '30d' | '90d'>('30d');
  const [summary, setSummary] = useState<Summary | null>(null);
  const [usage, setUsage] = useState<Usage | null>(null);
  const [dailyData, setDailyData] = useState<DailyData[]>([]);
  const [sessionsDaily, setSessionsDaily] = useState<SessionDailyData[]>([]);
  const [hourlyData, setHourlyData] = useState<HourlyData[]>([]);
  const [queriesPerSession, setQueriesPerSession] = useState<SessionQueryData[]>([]);
  const [recentActivity, setRecentActivity] = useState<ActivityItem[]>([]);
  const [refreshing, setRefreshing] = useState(false);

  const days = period === '7d' ? 7 : period === '30d' ? 30 : 90;

  const loadData = useCallback(async () => {
    setRefreshing(true);
    try {
      const uf = userId ? `&user_id=${userId}` : '';
      const fetches = [
        authFetch(`${API}/api/v1/metrics/summary?days=${days}${uf}`),
        authFetch(`${API}/api/v1/metrics/queries-per-day?days=${days}${uf}`),
        authFetch(`${API}/api/v1/metrics/sessions-per-day?days=${days}${uf}`),
        authFetch(`${API}/api/v1/metrics/queries-per-hour?days=${days}${uf}`),
        authFetch(`${API}/api/v1/metrics/queries-per-session?days=${days}${uf}`),
        authFetch(`${API}/api/v1/metrics/recent-activity?limit=15${uf}`),
      ];
      if (userId) {
        fetches.push(authFetch(`${API}/api/v1/conversations/usage?user_id=${userId}`));
      }

      const results = await Promise.all(fetches);
      const [sumRes, dailyRes, sessionsDayRes, hourlyRes, qpsRes, actRes] = results;

      if (sumRes.ok) setSummary(await sumRes.json());
      if (dailyRes.ok) setDailyData(await dailyRes.json());
      if (sessionsDayRes.ok) setSessionsDaily(await sessionsDayRes.json());
      if (hourlyRes.ok) setHourlyData(await hourlyRes.json());
      if (qpsRes.ok) setQueriesPerSession(await qpsRes.json());
      if (actRes.ok) setRecentActivity(await actRes.json());

      if (results[6] && results[6].ok) {
        setUsage(await results[6].json());
      }
    } catch (err) {
      console.error('Error loading metrics:', err);
    } finally {
      setRefreshing(false);
    }
  }, [days, userId]);

  useEffect(() => { loadData(); }, [loadData]);

  const metricCards = [
    { label: 'Mis Sesiones', value: summary?.total_sessions ?? 0, icon: MessageSquare },
    { label: 'Mis Consultas', value: summary?.total_queries ?? 0, icon: Search },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mi Dashboard</h1>
          <p className="text-sm text-gray-500 mt-1">Tu actividad personal en el Asistente de Apoyo Financiero</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={loadData}
            disabled={refreshing}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
            title="Refrescar datos"
          >
            <RefreshCw className={`w-4 h-4 text-gray-500 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
          <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
            {(['7d', '30d', '90d'] as const).map(p => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                  period === p ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {p === '7d' ? '7 días' : p === '30d' ? '30 días' : '90 días'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Usage limits - daily progress */}
      {usage && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-900">Tu consumo de hoy</h3>
            <span className="text-[10px] text-gray-400">Los contadores se reinician cada día a las 00:00</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <ProgressBar
              label="Sesiones"
              icon={MessageSquare}
              used={usage.sesiones.usado}
              limit={usage.sesiones.limite}
              color="#4F46E5"
            />
            <ProgressBar
              label="Consultas"
              icon={Hash}
              used={usage.consultas.usado}
              limit={usage.consultas.limite}
              color="#0891B2"
            />
            <ProgressBar
              label="Tokens"
              icon={Zap}
              used={usage.tokens.usado}
              limit={usage.tokens.limite}
              color="#7C3AED"
              suffix="tokens"
            />
          </div>
        </div>
      )}

      {/* Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {metricCards.map((m, i) => {
          const Icon = m.icon;
          return (
            <motion.div
              key={m.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
              className="metric-card"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#FFF3D0' }}>
                  <Icon className="w-5 h-5" style={{ color: '#EDAA00' }} />
                </div>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {typeof m.value === 'number' ? m.value.toLocaleString() : m.value}
              </p>
              <p className="text-xs text-gray-500 mt-1">{m.label}</p>
            </motion.div>
          );
        })}
      </div>

      {/* Charts Row 1: Consultas por día + Sesiones por día */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Consultas por día - Barras */}
        <div className="bg-white rounded-xl p-5 border border-gray-100" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Mis consultas por día</h3>
          {dailyData.length === 0 ? (
            <div className="flex items-center justify-center h-[260px] text-sm text-gray-400">
              Sin datos para el periodo seleccionado
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={dailyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11, fill: '#94A3B8' }}
                  tickLine={false}
                  axisLine={false}
                  interval={Math.max(0, Math.floor(dailyData.length / 8))}
                />
                <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ borderRadius: '8px', border: '1px solid #E2E8F0', fontSize: '12px' }}
                  cursor={{ fill: 'rgba(237, 170, 0, 0.08)' }}
                />
                <Bar dataKey="consultas" fill="#EDAA00" radius={[4, 4, 0, 0]} name="Consultas" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Sesiones por día - Barras */}
        <div className="bg-white rounded-xl p-5 border border-gray-100" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Mis sesiones por día</h3>
          {sessionsDaily.length === 0 ? (
            <div className="flex items-center justify-center h-[260px] text-sm text-gray-400">
              Sin datos para el periodo seleccionado
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={sessionsDaily}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11, fill: '#94A3B8' }}
                  tickLine={false}
                  axisLine={false}
                  interval={Math.max(0, Math.floor(sessionsDaily.length / 8))}
                />
                <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ borderRadius: '8px', border: '1px solid #E2E8F0', fontSize: '12px' }}
                  cursor={{ fill: 'rgba(79, 70, 229, 0.08)' }}
                />
                <Bar dataKey="sesiones" fill="#4F46E5" radius={[4, 4, 0, 0]} name="Sesiones" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Charts Row 2: Actividad por hora */}
      <div className="bg-white rounded-xl p-5 border border-gray-100" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Mi actividad por hora</h3>
        {hourlyData.length === 0 ? (
          <div className="flex items-center justify-center h-[220px] text-sm text-gray-400">
            Sin datos
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={hourlyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
              <XAxis dataKey="hour" tick={{ fontSize: 10, fill: '#94A3B8' }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip
                contentStyle={{ borderRadius: '8px', border: '1px solid #E2E8F0', fontSize: '12px' }}
                cursor={{ fill: 'rgba(27, 42, 74, 0.06)' }}
              />
              <Bar dataKey="consultas" fill="#1B2A4A" radius={[4, 4, 0, 0]} name="Consultas" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Consultas por sesión */}
      <div className="bg-white rounded-xl p-5 border border-gray-100" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Consultas por sesión</h3>
        {queriesPerSession.length === 0 ? (
          <div className="flex items-center justify-center h-[220px] text-sm text-gray-400">
            Sin sesiones en el periodo seleccionado
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(220, queriesPerSession.length * 32 + 40)}>
            <BarChart data={queriesPerSession} layout="vertical" margin={{ left: 10, right: 20, top: 5, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11, fill: '#94A3B8' }} tickLine={false} axisLine={false} allowDecimals={false} />
              <YAxis
                type="category"
                dataKey="session"
                tick={{ fontSize: 10, fill: '#64748B' }}
                tickLine={false}
                axisLine={false}
                width={180}
              />
              <Tooltip
                contentStyle={{ borderRadius: '8px', border: '1px solid #E2E8F0', fontSize: '12px' }}
                formatter={(value: number) => [`${value} mensajes`, 'Consultas']}
                labelFormatter={(label: string) => label}
                cursor={{ fill: 'rgba(8, 145, 178, 0.06)' }}
              />
              <Bar dataKey="consultas" fill="#0891B2" radius={[0, 4, 4, 0]} name="Consultas" barSize={18} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Activity */}
      <div className="bg-white rounded-xl p-5 border border-gray-100" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Mi actividad reciente</h3>
        {recentActivity.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-8">Sin actividad registrada</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {recentActivity.map(a => {
              const Icon = ACTIVITY_ICONS[a.type] || Activity;
              return (
                <div key={a.id} className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-gray-50">
                    <Icon className="w-4 h-4 text-gray-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-700">
                      <span className="font-medium">{a.user}</span>
                      {' '}{a.description}
                    </p>
                    <p className="text-[10px] text-gray-400 mt-0.5">{formatTime(a.timestamp)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
